import { getSafeUrl } from "../../../utils/helpers.js";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import HouseDummyImg from "../../../assets/images/houseDummy.webp";
import bed from "@images/Bed.svg";
import bathroom from "@images/Bathtub.svg";
import ruler from "@images/ruler.svg";
import { useUserState } from "../../../Contexts/UserContext";
const PropertySearchMap = ({
  houses = [],
  centerLat,
  centerLng,
  onLocationChange,
  height = "500px",
  forceRecenter,
}) => {
  const {isLoggedIn } = useUserState();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const overlaysRef = useRef([]);

  const isDraggingRef = useRef(false);
  const hasUserMovedMapRef = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    hasUserMovedMapRef.current = false;
  }, [forceRecenter]);

  /* -------------------- LOAD MAP -------------------- */
  useEffect(() => {
    if (!window.google || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: {
        lat: Number(centerLat) || 36.7833096,
        lng: Number(centerLng) || -119.4293701,
      },
      zoom: 13,
    });

    map.addListener("dragstart", () => {
      isDraggingRef.current = true;
      hasUserMovedMapRef.current = true; // ⭐ mark user interaction
    });

    map.addListener("dragend", () => {
      if (!isDraggingRef.current) return;

      const center = map.getCenter();
      if (center && onLocationChange) {
        onLocationChange(center.lat(), center.lng());
      }

      isDraggingRef.current = false;
    });

    mapInstanceRef.current = map;
  }, []);

  /* -------------------- UPDATE CENTER (AUTO ONLY ON SEARCH) -------------------- */
  useEffect(() => {
    if (!mapInstanceRef.current || !houses.length) return;

    // 🚫 DO NOT recenter if user already moved the map
    if (hasUserMovedMapRef.current) return;

    const bounds = new google.maps.LatLngBounds();

    houses.forEach((house) => {
      if (house.latitude && house.longitude) {
        bounds.extend({
          lat: Number(house.latitude),
          lng: Number(house.longitude),
        });
      }
    });

    if (houses.length === 1) {
      mapInstanceRef.current.setCenter(bounds.getCenter());
      mapInstanceRef.current.setZoom(15);
    } else {
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [houses]);

  /* -------------------- RENDER MARKERS -------------------- */
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    houses.forEach((house, index) => {
      if (!house.latitude || !house.longitude) return;

      const offset = index * 0.00008;
      const position = {
        lat: Number(house.latitude) + offset,
        lng: Number(house.longitude) + offset,
      };

      const marker = new google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
      });

      class PriceOverlay extends google.maps.OverlayView {
        onAdd() {
          this.div = document.createElement("div");
          this.div.innerHTML = `$${house.monthly_price}`;
          this.div.style.cssText = `
          background:#0474ba;
          color:#fff;
          padding:4px 10px;
          border-radius:20px;
          font-size:13px;
          font-weight:bold;
          white-space:nowrap;
          cursor:pointer;
          position:absolute;
          transform:translate(-50%,-50%);
        `;

          this.div.onclick = (e) => {
            e.stopPropagation();
            openHouseInfo(marker, house);
          };

          this.getPanes().overlayMouseTarget.appendChild(this.div);
        }

        draw() {
          const projection = this.getProjection();
          const point = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(position.lat, position.lng)
          );
          if (point) {
            this.div.style.left = point.x + "px";
            this.div.style.top = point.y + "px";
          }
        }

        onRemove() {
          this.div?.remove();
        }
      }

      const overlay = new PriceOverlay();
      overlay.setMap(mapInstanceRef.current);
      overlaysRef.current.push(overlay);
      markersRef.current.push(marker);
    });
  }, [houses]);

  /* -------------------- INFO WINDOW -------------------- */
  const openHouseInfo = (marker, house) => {
    infoWindowRef.current?.close();

    const content = `
    <div class="house-card" style="width:260px;cursor:pointer;font-family:Arial, sans-serif">
      <div class="property-listing" >
        <div class="property-sec">
          <div class="property-inner">
            <span class="type-pro">
              ${
                house.house_facility === "Customize"
                  ? "Unfurnished"
                  : house.house_facility
              }
            </span>
            <img 
              src="${
                house?.images?.length
                  ? getSafeUrl(house.images.at(-1))
                  : HouseDummyImg
              }"
              onerror="this.onerror=null;this.src='${HouseDummyImg}'"
              style="width:100%;height:130px;object-fit:cover;"
            />
            <div class="property-content">
               <div class="property-discription" style="margin-top:6px;">
                <h4 >${house.name}</h4>
                <p >
                  ${house.full_address} ${house.state || ""} ${
      house.country || ""
    }
                </p>
              </div>
              <div class="property-price" >
                <p class="pricing">
                  $${house.monthly_price}/<span>Month</span>
                </p>
              </div>
              <div class="property-overview">
                <ul >
                  <li><img src="${bed}"> ${house.bedroom_no} Bedroom</li>
                  <li><img src="${bathroom}"> ${house.bathroom_no} Bathroom</li>
                      <li><img src="${ruler}"> ${house.area} SQ FT</li>
                  <li><i class="fa-regular fa-map" style="color:rgb(4,116,186);margin-right:4px;"></i> ${
                    house?.distance
                  } miles</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

    const infoWindow = new google.maps.InfoWindow({ content });

    infoWindow.addListener("domready", () => {
      const card = document.querySelector(".house-card");
      if (card) {
        card.addEventListener("click", () =>
          navigate(`/property_detail/${house.id}`)
        );
      }
    });

    infoWindow.open(mapInstanceRef.current, marker);
    infoWindowRef.current = infoWindow;
  };

  return <div ref={mapRef} className="map-search" />;
};

export default PropertySearchMap;
